import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { DIAGNOSTIC_PROFILES } from '../../constants/diagnostic-profiles.const';
import {
  DEEP_DEBUG_BODY_MAX,
  DEEP_DEBUG_BODY_WARN,
  DEEP_DEBUG_EVENTS_MAX,
  DEEP_DEBUG_EVENTS_MIN,
  DEEP_DEBUG_EVENTS_WARN,
  DiagnosticDeepDebugConfigService,
} from '../../services/diagnostic-deep-debug-config.service';

interface DeepDebugSettingsForm {
  requestBodyMaxChars: FormControl<number>;
  responseBodyMaxChars: FormControl<number>;
  captureHeaders: FormControl<boolean>;
  maxEvents: FormControl<number>;
}

const DEEP_DEBUG_PROFILE = DIAGNOSTIC_PROFILES.find(
  (p) => p.name === 'deepDebug'
);
const DEEP_DEBUG_STATIC_PAYLOAD = DEEP_DEBUG_PROFILE?.apiPayload ?? {};
const DEEP_DEBUG_STATIC_MAX_EVENTS = DEEP_DEBUG_PROFILE?.maxEvents ?? 500;

@Component({
  selector: 'app-diagnostic-deep-debug-settings',
  templateUrl: './diagnostic-deep-debug-settings.component.html',
  styleUrls: ['./diagnostic-deep-debug-settings.component.scss'],
})
export class DiagnosticDeepDebugSettingsComponent {
  readonly bodyMax = DEEP_DEBUG_BODY_MAX;
  readonly bodyWarn = DEEP_DEBUG_BODY_WARN;
  readonly eventsMin = DEEP_DEBUG_EVENTS_MIN;
  readonly eventsMax = DEEP_DEBUG_EVENTS_MAX;
  readonly eventsWarn = DEEP_DEBUG_EVENTS_WARN;

  form: FormGroup<DeepDebugSettingsForm>;

  get showSizeWarning(): boolean {
    return (
      this.form.value.requestBodyMaxChars! > this.bodyWarn ||
      this.form.value.responseBodyMaxChars! > this.bodyWarn
    );
  }

  get showEventsWarning(): boolean {
    return this.form.value.maxEvents! > this.eventsWarn;
  }

  constructor(
    private readonly configService: DiagnosticDeepDebugConfigService,
    private readonly dialogRef: MatDialogRef<DiagnosticDeepDebugSettingsComponent>
  ) {
    const current = configService.read();
    this.form = new FormGroup<DeepDebugSettingsForm>({
      requestBodyMaxChars: new FormControl<number>(
        current?.requestBodyMaxChars ??
          DEEP_DEBUG_STATIC_PAYLOAD.requestBodyMaxChars ??
          0,
        {
          nonNullable: true,
          validators: [Validators.min(0), Validators.max(DEEP_DEBUG_BODY_MAX)],
        }
      ),
      responseBodyMaxChars: new FormControl<number>(
        current?.responseBodyMaxChars ??
          DEEP_DEBUG_STATIC_PAYLOAD.responseBodyMaxChars ??
          0,
        {
          nonNullable: true,
          validators: [Validators.min(0), Validators.max(DEEP_DEBUG_BODY_MAX)],
        }
      ),
      captureHeaders: new FormControl<boolean>(
        current?.captureHeaders ??
          DEEP_DEBUG_STATIC_PAYLOAD.captureHeaders ??
          false,
        { nonNullable: true }
      ),
      maxEvents: new FormControl<number>(
        current?.maxEvents ?? DEEP_DEBUG_STATIC_MAX_EVENTS,
        {
          nonNullable: true,
          validators: [
            Validators.min(DEEP_DEBUG_EVENTS_MIN),
            Validators.max(DEEP_DEBUG_EVENTS_MAX),
          ],
        }
      ),
    });
  }

  save(): void {
    if (this.form.invalid) return;
    this.configService.write(this.form.getRawValue());
    this.dialogRef.close(true);
  }

  resetToDefault(): void {
    this.configService.clear();
    this.form.patchValue({
      requestBodyMaxChars: DEEP_DEBUG_STATIC_PAYLOAD.requestBodyMaxChars ?? 0,
      responseBodyMaxChars: DEEP_DEBUG_STATIC_PAYLOAD.responseBodyMaxChars ?? 0,
      captureHeaders: DEEP_DEBUG_STATIC_PAYLOAD.captureHeaders ?? false,
      maxEvents: DEEP_DEBUG_STATIC_MAX_EVENTS,
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
